"use client";

import { useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSnapshot } from "valtio";
import { rootStore } from "@store/rootStore";
import { Track } from "@/src/lib/tracking";
import { useOspApiClientProvider } from "@hooks/useOspApiClientProvider";
import { adaptAccountReadUnreadList } from "../accountReadAdapter";
import { accountReadQueryKeys } from "./accountReadQueryKeys";
import type { AccountReadUnreadSummary } from "../accountReadTypes";
import { mockStore } from "../mock";

/**
 * Mock fallback switch: set NEXT_PUBLIC_ACCOUNT_READ_USE_MOCK=1 to serve
 * reads from the in-memory mock store (dev/demo only). Default: real API.
 */
const USE_MOCK = process.env.NEXT_PUBLIC_ACCOUNT_READ_USE_MOCK === "1";
const INCLUDE_READ_ACCOUNT_READS_FOR_QA =
  process.env.NEXT_PUBLIC_ACCOUNT_READ_INCLUDE_READ === "1";

export function markMockAccountReadAsRead(reportId: string): void {
  mockStore.markAsRead(reportId);
}

export function resetMockAccountReadStatus(): void {
  mockStore.resetReadStatus();
}

export function isMockAccountRead(reportId: string): boolean {
  return mockStore.isRead(reportId);
}

export interface UseAccountReadUnreadResult {
  data: AccountReadUnreadSummary[];
  reports: AccountReadUnreadSummary[];
  count: number;
  latest?: AccountReadUnreadSummary;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
}

export function useAccountReadUnread(
  ownerId?: string,
): UseAccountReadUnreadResult {
  const { accountReadsApi } = useOspApiClientProvider();
  const { ownerInfo } = useSnapshot(rootStore.user);
  const loggedIn = Boolean(ownerInfo?.id);
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: accountReadQueryKeys.unread(ownerId),
    enabled: USE_MOCK || (Boolean(accountReadsApi) && loggedIn),
    queryFn: async (): Promise<AccountReadUnreadSummary[]> => {
      if (USE_MOCK) {
        const raw = mockStore.getUnreadRawDtos();
        return adaptAccountReadUnreadList(raw);
      }
      if (!accountReadsApi) {
        throw new Error("ACCOUNT_READS_CLIENT_NOT_READY");
      }

      const res = await accountReadsApi.accountReads.getAccountReads({
        unread_only: INCLUDE_READ_ACCOUNT_READS_FOR_QA ? undefined : true,
      });
      const responseBody = (res as unknown as { data?: unknown })?.data ?? res;
      const body = ((responseBody as { data?: unknown })?.data ??
        responseBody) as {
        unread_count?: number;
        reads?: Record<string, unknown>[];
      };
      const rawList = Array.isArray(body.reads) ? body.reads : [];
      return adaptAccountReadUnreadList(rawList as never[]);
    },
    staleTime: 0,
    retry: 1,
  });

  // The explicit detail-surface acknowledgement invalidates this query. Focus
  // refresh is retained for cross-device read acknowledgements.
  useEffect(() => {
    const refresh = () =>
      void queryClient.invalidateQueries({
        queryKey: accountReadQueryKeys.unread(ownerId),
      });
    window.addEventListener("focus", refresh);
    return () => window.removeEventListener("focus", refresh);
  }, [queryClient, ownerId]);

  const { data, isError, error, isPending, refetch } = query;
  const reportedFailRef = useRef(false);

  useEffect(() => {
    if (isError && !reportedFailRef.current) {
      reportedFailRef.current = true;
      Track.error(error, { surface: "acct_read_unread" });
      Track.event("acct_read_entry_load_fail");
    }
  }, [isError, error]);

  const reports = isError || !data ? [] : data;
  const count = reports.length;
  const latest =
    reports.length > 0
      ? [...reports].sort(
          (a, b) =>
            new Date(b.publishedAt).getTime() -
            new Date(a.publishedAt).getTime(),
        )[0]
      : undefined;

  return {
    data: reports,
    reports,
    count,
    latest,
    isLoading: query.isFetching || isPending,
    isError,
    refetch,
  };
}
